import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Shared hook for all Admin Panel tables to handle caching, pagination, sorting, and filtering.
 * Uses localStorage for caching to ensure data persists across full page reloads, mimicking the My Orders architecture.
 */
export const useAdminTable = (fetchData, cacheKey, initialParams = {}) => {
    const fetchDataRef = useRef(fetchData);

    useEffect(() => {
        fetchDataRef.current = fetchData;
    }, [fetchData]);

    const getStorageKey = (params) => `${cacheKey}_${JSON.stringify(params)}`;

    const [data, setData] = useState(() => {
        try {
            const cached = localStorage.getItem(getStorageKey(initialParams));
            return cached ? JSON.parse(cached).data : [];
        } catch (e) { return []; }
    });

    const [total, setTotal] = useState(() => {
        try {
            const cached = localStorage.getItem(getStorageKey(initialParams));
            return cached ? JSON.parse(cached).total : 0;
        } catch (e) { return 0; }
    });

    const [extraData, setExtraData] = useState(() => {
        try {
            const cached = localStorage.getItem(getStorageKey(initialParams));
            return cached ? JSON.parse(cached).extraData : null;
        } catch (e) { return null; }
    });

    // If we have cached data, we don't block the initial render with a loading skeleton
    const hasInitialCache = () => {
        try { return !!localStorage.getItem(getStorageKey(initialParams)); } catch (e) { return false; }
    };

    const [loading, setLoading] = useState(!hasInitialCache());
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    const [params, setParams] = useState({
        page: 1,
        limit: 10,
        sortField: 'createdAt',
        sortOrder: 'desc',
        search: '',
        ...initialParams
    });

    const isMounted = useRef(true);

    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    const loadData = useCallback(async (currentParams, silent = false) => {
        try {
            const storageKey = getStorageKey(currentParams);
            const cached = localStorage.getItem(storageKey);

            // Instant render from cache
            if (cached && !silent) {
                try {
                    const parsed = JSON.parse(cached);
                    if (isMounted.current) {
                        setData(parsed.data || []);
                        setTotal(parsed.total || 0);
                        setExtraData(parsed.extraData || null);
                        setLoading(false);
                    }
                } catch (e) { }
            } else if (!silent) {
                setLoading(true);
            }

            setRefreshing(true);
            setError(null);

            const result = await fetchDataRef.current(currentParams);

            if (isMounted.current) {
                setData(result.data || []);
                setTotal(result.total || 0);
                
                const { data: _d, total: _t, ...rest } = result;
                setExtraData(rest);

                // Update Cache in background
                try {
                    localStorage.setItem(storageKey, JSON.stringify({
                        data: result.data || [],
                        total: result.total || 0,
                        extraData: rest,
                        params: currentParams,
                        timestamp: Date.now()
                    }));
                } catch (e) { }
            }
        } catch (err) {
            console.error(`Error loading data for ${cacheKey}:`, err);
            if (isMounted.current) setError(err);
        } finally {
            if (isMounted.current) {
                setLoading(false);
                setRefreshing(false);
            }
        }
    }, [cacheKey]);

    useEffect(() => {
        // Clear stale data immediately when filter params change (not just page/limit)
        // This prevents old rows from showing while new filtered data loads
        const isFilterChange = true; // any param change triggers this
        const storageKey = getStorageKey(params);
        const hasCacheForThisParams = (() => {
            try { return !!localStorage.getItem(storageKey); } catch(e) { return false; }
        })();

        // If no cache exists for these exact params, clear data immediately
        if (!hasCacheForThisParams) {
            setData([]);
            setTotal(0);
        }

        const timer = setTimeout(() => {
            loadData(params, false);
        }, 300);
        return () => clearTimeout(timer);
    }, [params, loadData]);

    const handleTableChange = (pagination, filters, sorter) => {
        setParams(prev => ({
            ...prev,
            page: pagination.current,
            limit: pagination.pageSize,
            sortField: sorter.field || prev.sortField,
            sortOrder: sorter.order === 'ascend' ? 'asc' : sorter.order === 'descend' ? 'desc' : prev.sortOrder,
            ...filters
        }));
    };

    const handleSearch = (value) => {
        setParams(prev => ({ ...prev, search: value, page: 1 }));
    };

    const refresh = () => loadData(params, true);

    return {
        data,
        setData,
        total,
        loading,
        refreshing,
        error,
        params,
        setParams,
        handleTableChange,
        handleSearch,
        refresh,
        extraData
    };
};
